import configs from "../../configs.js";
import { createDpopProof } from "./dpopProof.js";

const VERIFIER_PREFIX = "auth_bridge_verifier:";
const RETURN_TO_PREFIX = "auth_bridge_return_to:";
const REHYDRATE_ATTEMPT_PREFIX = "auth_bridge_rehydrate_attempt:";
const REHYDRATE_ATTEMPT_TTL_MS = 30 * 1000;
const BRIDGE_TOKEN_REUSE_SKEW_MS = 60 * 1000;
const DEFAULT_BRIDGE_TOKEN_TTL_MS = 5 * 60 * 1000;
const POPUP_WIDTH = 420;
const POPUP_HEIGHT = 585;
export const DASHBOARD_BRIDGE_TOKEN_EVENT = "editor:auth-bridge-token";
export const ACCOUNT_SUSPENDED_BRIDGE_ERROR = "account_suspended";

let dashboardBridgeToken = null;
let inFlightBridgeTokenRenewal = null;

function dispatchDashboardBridgeTokenEvent() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new window.CustomEvent(DASHBOARD_BRIDGE_TOKEN_EVENT));
  }
}

function base64Url(bytes) {
  const binary = Array.from(bytes, byte => String.fromCharCode(byte)).join("");
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function randomString() {
  const bytes = new Uint8Array(32);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
    return base64Url(bytes);
  }

  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

async function sha256Base64Url(value) {
  if (typeof crypto !== "undefined" && crypto.subtle) {
    const hash = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
    return base64Url(new Uint8Array(hash));
  }

  return value;
}

function getRequiredBridgeUrl() {
  const bridgeUrl = configs.authBridgeUrl();
  if (!bridgeUrl) {
    throw new Error("AUTH_BRIDGE_URL is not configured.");
  }

  return bridgeUrl;
}

function normalizeBasePath(basePath) {
  if (!basePath) {
    return "";
  }

  const trimmed = basePath.trim();
  if (!trimmed || trimmed === "/") {
    return "";
  }

  const normalized = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return normalized.replace(/\/+$/, "");
}

function getBridgeCallbackPath() {
  return `${normalizeBasePath(configs.ROUTER_BASE_PATH)}/auth/bridge/callback`;
}

export function isDashboardBridgeConfigured() {
  return !!configs.authBridgeUrl();
}

async function buildBridgeRequestHeaders(url, method) {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json"
  };

  headers.DPoP = await createDpopProof({ url, method });

  return headers;
}

export function getDashboardBridgeToken() {
  return getDashboardBridgeTokenEntry()?.token || null;
}

export function getDashboardBridgeTokenEntry() {
  if (!dashboardBridgeToken || dashboardBridgeToken.expiresAtMs - BRIDGE_TOKEN_REUSE_SKEW_MS <= Date.now()) {
    return null;
  }

  return dashboardBridgeToken;
}

export function setDashboardBridgeToken(token, expiresAtSeconds) {
  const normalizedToken = token && token.trim() ? token.trim() : "";
  const hadBridgeToken = Boolean(dashboardBridgeToken);
  dashboardBridgeToken = normalizedToken
    ? {
        token: normalizedToken,
        tokenType: "DPoP",
        expiresAtMs:
          Number.isFinite(Number(expiresAtSeconds)) && Number(expiresAtSeconds) > 0
            ? Number(expiresAtSeconds) * 1000
            : Date.now() + DEFAULT_BRIDGE_TOKEN_TTL_MS
      }
    : null;

  if (hadBridgeToken !== Boolean(dashboardBridgeToken)) {
    dispatchDashboardBridgeTokenEvent();
  }
}

export function clearDashboardBridgeToken() {
  inFlightBridgeTokenRenewal = null;
  setDashboardBridgeToken(null);
}

export function clearDashboardBridgeSessionState(
  storage = typeof sessionStorage !== "undefined" ? sessionStorage : undefined
) {
  if (!storage) {
    return;
  }

  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);
    if (
      key?.startsWith(VERIFIER_PREFIX) ||
      key?.startsWith(RETURN_TO_PREFIX) ||
      key?.startsWith(REHYDRATE_ATTEMPT_PREFIX)
    ) {
      storage.removeItem(key);
    }
  }
}

export function readDashboardBridgeCallbackState(state) {
  if (typeof sessionStorage === "undefined") {
    return null;
  }

  const codeVerifier = sessionStorage.getItem(`${VERIFIER_PREFIX}${state}`);
  const returnTo = sessionStorage.getItem(`${RETURN_TO_PREFIX}${state}`);

  return codeVerifier && returnTo ? { codeVerifier, returnTo } : null;
}

export function clearDashboardBridgeCallbackState(state) {
  if (typeof sessionStorage === "undefined") {
    return;
  }

  sessionStorage.removeItem(`${VERIFIER_PREFIX}${state}`);
  sessionStorage.removeItem(`${RETURN_TO_PREFIX}${state}`);
}

export function clearDashboardBridgeRehydrationAttempt(returnTo) {
  if (typeof sessionStorage === "undefined") {
    return;
  }

  sessionStorage.removeItem(`${REHYDRATE_ATTEMPT_PREFIX}${returnTo}`);
}

export async function prepareDashboardBridgeRequest(returnTo) {
  const state = randomString();
  const verifier = randomString();
  const codeChallenge = await sha256Base64Url(verifier);

  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem(`${VERIFIER_PREFIX}${state}`, verifier);
    sessionStorage.setItem(`${RETURN_TO_PREFIX}${state}`, returnTo);
  }

  const callbackUrl =
    typeof window !== "undefined" && window.location.origin && window.location.origin !== "null"
      ? new URL(getBridgeCallbackPath(), window.location.origin).toString()
      : returnTo;

  return {
    state,
    codeVerifier: verifier,
    codeChallenge,
    callbackUrl
  };
}

export async function buildDashboardBridgeStartUrl(returnTo, mode, options = {}) {
  const bridgeUrl = getRequiredBridgeUrl();
  const bridgeRequest = await prepareDashboardBridgeRequest(returnTo);
  const url = new URL("/auth/bridge/start", bridgeUrl);
  url.searchParams.set("client", configs.authBridgeClientId());
  url.searchParams.set("return_to", bridgeRequest.callbackUrl);
  url.searchParams.set("mode", mode);
  if (options.prompt) {
    url.searchParams.set("prompt", options.prompt);
  }
  url.searchParams.set("state", bridgeRequest.state);
  url.searchParams.set("code_challenge", bridgeRequest.codeChallenge);

  return url.toString();
}

export async function startDashboardBridgeRedirect(returnTo) {
  const url = await buildDashboardBridgeStartUrl(returnTo, "redirect");

  if (typeof window !== "undefined") {
    window.location.assign(url);
  }
}

export async function startDashboardBridgeRehydration(returnTo) {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
    return false;
  }

  const attemptKey = `${REHYDRATE_ATTEMPT_PREFIX}${returnTo}`;
  const lastAttempt = Number(sessionStorage.getItem(attemptKey));
  const now = Date.now();

  if (Number.isFinite(lastAttempt) && now - lastAttempt < REHYDRATE_ATTEMPT_TTL_MS) {
    return false;
  }

  sessionStorage.setItem(attemptKey, String(now));
  const url = await buildDashboardBridgeStartUrl(returnTo, "redirect", {
    prompt: "none"
  });
  window.location.assign(url);
  return true;
}

export function buildDashboardBridgeLogoutUrl(returnTo) {
  const url = new URL("/logout", getRequiredBridgeUrl());
  url.searchParams.set("client", configs.authBridgeClientId());
  url.searchParams.set("return_to", returnTo);
  return url.toString();
}

export function logoutWithDashboardBridge(options = {}) {
  const returnTo = options.logoutParams?.returnTo;
  const logoutUrl = returnTo
    ? buildDashboardBridgeLogoutUrl(returnTo)
    : new URL("/logout", getRequiredBridgeUrl()).toString();

  clearDashboardBridgeToken();
  clearDashboardBridgeSessionState();

  if (typeof window !== "undefined") {
    window.location.assign(logoutUrl);
  }
}

export function buildDashboardBridgePopupFeatures(metrics = typeof window !== "undefined" ? window : {}) {
  const screenX = Number.isFinite(metrics.screenX) ? Number(metrics.screenX) : 0;
  const screenY = Number.isFinite(metrics.screenY) ? Number(metrics.screenY) : 0;
  const viewportWidth = Number.isFinite(metrics.innerWidth)
    ? Number(metrics.innerWidth)
    : Number.isFinite(metrics.outerWidth)
    ? Number(metrics.outerWidth)
    : POPUP_WIDTH;
  const viewportHeight = Number.isFinite(metrics.innerHeight)
    ? Number(metrics.innerHeight)
    : Number.isFinite(metrics.outerHeight)
    ? Number(metrics.outerHeight)
    : POPUP_HEIGHT;
  const left = Math.max(0, Math.round(screenX + (viewportWidth - POPUP_WIDTH) / 2));
  const top = Math.max(0, Math.round(screenY + (viewportHeight - POPUP_HEIGHT) / 2));

  return ["popup=yes", `width=${POPUP_WIDTH}`, `height=${POPUP_HEIGHT}`, `left=${left}`, `top=${top}`].join(",");
}

export async function exchangeDashboardBridgeCode(code, state, codeVerifier) {
  const bridgeUrl = getRequiredBridgeUrl();
  const url = new URL("/nri/api/auth/bridge/exchange", bridgeUrl);
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: await buildBridgeRequestHeaders(url.toString(), "POST"),
    body: JSON.stringify({
      client: configs.authBridgeClientId(),
      code,
      code_verifier: codeVerifier
    })
  });

  if (!response.ok) {
    const error = new Error(`Auth bridge exchange failed: ${response.status}`);
    error.status = response.status;
    throw error;
  }

  const payload = await response.json();
  const token = typeof payload.access_token === "string" ? payload.access_token : "";
  if (!token) {
    throw new Error("Auth bridge exchange returned an empty token.");
  }

  const expiresAt =
    "expires_at" in payload && Number.isFinite(Number(payload.expires_at)) ? Number(payload.expires_at) : undefined;
  setDashboardBridgeToken(token, expiresAt);
  return token;
}

export async function loginWithDashboardBridgePopup(returnTo) {
  const bridgeUrl = getRequiredBridgeUrl();
  const startUrl = await buildDashboardBridgeStartUrl(returnTo, "popup");

  const message = await new Promise((resolve, reject) => {
    const popup = window.open(startUrl, "onland-auth-bridge", buildDashboardBridgePopupFeatures());
    if (!popup) {
      reject(Object.assign(new Error("auth_popup_blocked"), { code: "auth_popup_blocked" }));
      return;
    }

    const expectedOrigin = new URL(bridgeUrl).origin;
    const expectedState = new URL(startUrl).searchParams.get("state") || "";
    let timeout = null;
    function onMessage(event) {
      if (event.origin !== expectedOrigin) {
        return;
      }

      if (
        event.data?.type === "onland.auth_bridge.error" &&
        event.data?.code === ACCOUNT_SUSPENDED_BRIDGE_ERROR &&
        String(event.data?.state || "") === expectedState
      ) {
        window.clearTimeout(timeout);
        window.removeEventListener("message", onMessage);
        popup.close();
        logoutWithDashboardBridge({ logoutParams: { returnTo } });
        reject(
          Object.assign(new Error(ACCOUNT_SUSPENDED_BRIDGE_ERROR), {
            code: ACCOUNT_SUSPENDED_BRIDGE_ERROR,
            popup
          })
        );
        return;
      }

      if (event.data?.type !== "onland.auth_bridge.code") {
        return;
      }

      window.clearTimeout(timeout);
      window.removeEventListener("message", onMessage);
      popup.close();
      resolve({
        code: String(event.data.code || ""),
        state: String(event.data.state || "")
      });
    }

    timeout = window.setTimeout(() => {
      window.removeEventListener("message", onMessage);
      popup.close();
      reject(Object.assign(new Error("auth_popup_timeout"), { code: "auth_popup_timeout", popup }));
    }, Number(configs.AUTH0_POPUP_TIMEOUT_SECONDS || 300) * 1000);

    window.addEventListener("message", onMessage);
  });

  const callbackState = readDashboardBridgeCallbackState(message.state);
  if (!callbackState) {
    throw new Error("Auth bridge callback state is missing.");
  }

  try {
    return await exchangeDashboardBridgeCode(message.code, message.state, callbackState.codeVerifier);
  } finally {
    clearDashboardBridgeCallbackState(message.state);
  }
}

export async function renewDashboardBridgeToken() {
  return (await renewDashboardBridgeTokenEntry())?.token || null;
}

export async function renewDashboardBridgeTokenEntry() {
  const cachedToken = getDashboardBridgeTokenEntry();
  if (cachedToken) {
    return cachedToken;
  }

  if (!inFlightBridgeTokenRenewal) {
    inFlightBridgeTokenRenewal = (async () => {
      const url = new URL("/auth/bridge/token", getRequiredBridgeUrl());
      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: await buildBridgeRequestHeaders(url.toString(), "POST"),
        body: JSON.stringify({
          client: configs.authBridgeClientId()
        })
      });

      if (response.status === 204 || response.status === 401) {
        clearDashboardBridgeToken();
        return null;
      }

      if (!response.ok) {
        clearDashboardBridgeToken();
        return null;
      }

      const payload = await response.json();
      const token = typeof payload.access_token === "string" ? payload.access_token : "";
      if (!token) {
        clearDashboardBridgeToken();
        return null;
      }

      const expiresAt =
        "expires_at" in payload && Number.isFinite(Number(payload.expires_at)) ? Number(payload.expires_at) : undefined;
      setDashboardBridgeToken(token, expiresAt);
      return getDashboardBridgeTokenEntry();
    })()
      .finally(() => {
        inFlightBridgeTokenRenewal = null;
      });
  }

  return inFlightBridgeTokenRenewal;
}

function getDashboardBridgeDebugStatus() {
  const reusable = Boolean(getDashboardBridgeToken());
  const expiresAtMs = dashboardBridgeToken?.expiresAtMs || null;
  const secondsUntilExpiry = expiresAtMs ? Math.ceil((expiresAtMs - Date.now()) / 1000) : null;

  return {
    configured: isDashboardBridgeConfigured(),
    hasToken: Boolean(dashboardBridgeToken),
    expiresAtMs,
    expiresAtIso: expiresAtMs ? new Date(expiresAtMs).toISOString() : null,
    secondsUntilExpiry,
    reusable,
    renewalNeeded: !reusable,
    reuseSkewSeconds: BRIDGE_TOKEN_REUSE_SKEW_MS / 1000
  };
}

function setDashboardBridgeTokenExpiryForDebug(expiresAtMs) {
  if (dashboardBridgeToken) {
    dashboardBridgeToken = {
      ...dashboardBridgeToken,
      expiresAtMs
    };
  }

  return getDashboardBridgeDebugStatus();
}

export function installDashboardBridgeDebugHelpers() {
  if (typeof window === "undefined") {
    return;
  }

  window.__xrhubBridgeDebug = {
    status: getDashboardBridgeDebugStatus,
    expireIn(seconds) {
      const normalizedSeconds = Number.isFinite(Number(seconds)) ? Number(seconds) : DEFAULT_BRIDGE_TOKEN_TTL_MS / 1000;
      return setDashboardBridgeTokenExpiryForDebug(Date.now() + normalizedSeconds * 1000);
    },
    expireNow() {
      return setDashboardBridgeTokenExpiryForDebug(Date.now() - 1000);
    },
    clear() {
      clearDashboardBridgeToken();
      return getDashboardBridgeDebugStatus();
    },
    async renew() {
      const token = await renewDashboardBridgeToken();
      return {
        renewed: Boolean(token),
        status: getDashboardBridgeDebugStatus()
      };
    }
  };
}

installDashboardBridgeDebugHelpers();
